import {
  Image,
  Page,
  View,
  Text,
  LinearGradient,
  Link,
  Svg,
  Defs,
  Stop,
  Rect,
} from '@react-pdf/renderer';
import { styles } from './styles';
import {
  AgendaSpeaker,
  PublicEventDetailsResponse,
  PublicSessionItem,
} from '@hubs101/booking-api-skd-client/lib/types';
import { useTranslation } from 'react-i18next';
import { getImageSrc } from '@utils/fn';
import { iconsMap } from '@composites/PDF/constants';
import { globalStyles } from '../../styles';
import { resolveImageColor } from '../..';
import { APP_HOST } from '@utils/apiHooks';
import worldIcon from '@assets/icons/world_icon.png';
import mailIcon from '@assets/icons/mail_icon.png';
import { SPONSOR_EMAIL } from '@utils/constants';
import { SafeImage } from '@composites/PDF/AgendaPdfContent/components/SafeImage';
import { useMemo } from 'react';
import { htmlToText } from '@utils/htmlParser';

export interface KeynotePageProps {
  event: PublicEventDetailsResponse;
  keynoteSessions: PublicSessionItem[];
}

const KeynotePage = ({
  event,
  keynoteSessions,
  seriesLogoNeg,
  seriesLogoPos,
}: KeynotePageProps & { seriesLogoNeg: string; seriesLogoPos: string }) => {
  const { t } = useTranslation();

  const portfolioUrl = event?.portfolio?.slug
    ? `${APP_HOST}${t('mappable-company-routes.our-product-portfolio-url')}/${event.portfolio.slug}`
    : '';

  const keynoteSpeakers: Array<
    AgendaSpeaker & {
      sessionTitle: string;
      sessionDescription: string;
      sessionId: string;
    }
  > = useMemo(() => {
    return keynoteSessions?.flatMap(
      session =>
        session.speakers
          ?.map(speaker => ({
            ...speaker,
            sessionTitle: session.title,
            sessionDescription: session.description,
            sessionId: session.id,
          }))
          ?.filter(speaker => {
            if (!speaker?.images?.picture_additional) {
              return false;
            }

            return true;
          }) ?? []
    );
  }, [keynoteSessions]);

  return (
    <>
      {keynoteSpeakers?.length > 0 &&
        keynoteSpeakers.map((speakerDetails, index) => {
          const contact = { ...(speakerDetails as any)?.contact };

          return (
            <Page
              key={`keynote-page-${speakerDetails.sessionId}-${speakerDetails.profile}`}
              size={{ width: 1440, height: 800 }}
              style={{ ...styles.page, overflow: 'hidden' }}
            >
              {/* Linear Gradient Background */}
              <Svg
                width={1380}
                height={740}
                style={{
                  position: 'absolute',
                  top: 30,
                  left: 30,
                  zIndex: 0,
                  borderRadius: '24px',
                }}
              >
                <Defs>
                  <LinearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                    <Stop
                      offset="0%"
                      stopColor={event?.design?.themeColor || '#0F91AE'}
                    />
                    <Stop offset="100%" stopColor="#2C2F38" />
                  </LinearGradient>
                </Defs>

                <Rect width="100%" height="100%" fill="url(#grad1)" />
              </Svg>

              <SafeImage
                style={styles.speakerImage}
                src={getImageSrc(speakerDetails?.images?.picture_additional)}
              />

              <View
                style={{
                  ...styles.leftColumn,
                }}
              ></View>

              <View style={{ ...styles.rightColumn, position: 'relative' }}>
                <View style={{ paddingLeft: 40, paddingRight: 40 }}>
                  <Text style={styles.title}>{t('keynote')}</Text>

                  <View
                    style={{
                      ...globalStyles.lineBar,
                      borderColor: event?.design?.themeColor ?? '#0F91AE',
                      marginTop: 20,
                      marginLeft: 0,
                      width: 140,
                    }}
                  ></View>

                  <Text style={styles.sessionDescription}>
                    {htmlToText(speakerDetails?.sessionDescription ?? '')}
                  </Text>

                  <SafeImage
                    src={getImageSrc(speakerDetails?.company?.logo ?? '')}
                    style={{
                      width: 180,
                      height: 80,
                      objectFit: 'contain',
                      backgroundColor: 'white',
                      borderRadius: 10,
                      marginBottom: 40,
                    }}
                  />

                  <Text style={styles.speakerName}>
                    {speakerDetails?.first_name ?? ''}{' '}
                    {speakerDetails?.last_name ?? ''}
                  </Text>

                  <Text style={styles.speakerJobTitle}>
                    {speakerDetails?.job?.title ?? ''}
                  </Text>

                  <View style={styles.socialIconsContainer}>
                    {contact.linkedin && (
                      <Link
                        src={contact.linkedin}
                        style={{ textDecoration: 'none' }}
                      >
                        <Text style={{ ...styles.iconWhite }}>
                          {iconsMap.social_linkedin || iconsMap.default}
                        </Text>
                      </Link>
                    )}
                    {contact.xing && (
                      <Link
                        src={contact.xing}
                        style={{ textDecoration: 'none' }}
                      >
                        <Text style={{ ...styles.iconWhite }}>
                          {iconsMap.social_xing || iconsMap.default}
                        </Text>
                      </Link>
                    )}
                    {contact.twitter && (
                      <Link
                        src={contact.twitter}
                        style={{ textDecoration: 'none' }}
                      >
                        <Text style={{ ...styles.iconWhite }}>
                          {iconsMap.social_twitter || iconsMap.default}
                        </Text>
                      </Link>
                    )}
                    {contact.facebook && (
                      <Link
                        src={contact.facebook}
                        style={{ textDecoration: 'none' }}
                      >
                        <Text style={{ ...styles.iconWhite }}>
                          {iconsMap.social_facebook || iconsMap.default}
                        </Text>
                      </Link>
                    )}
                    {contact.instagram && (
                      <Link
                        src={contact.instagram}
                        style={{ textDecoration: 'none' }}
                      >
                        <Text style={{ ...styles.iconWhite }}>
                          {iconsMap.social_instagram || iconsMap.default}
                        </Text>
                      </Link>
                    )}
                  </View>
                </View>

                <View
                  style={{
                    position: 'absolute',
                    bottom: 50,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    gap: 26,
                    alignItems: 'flex-end',
                    width: '100%',
                    paddingLeft: 60,
                    paddingRight: 20,
                  }}
                >
                  <Link src={portfolioUrl} style={{ textDecoration: 'none' }}>
                    {seriesLogoNeg ? (
                      <Image
                        style={{
                          width: 120,
                          height: 40,
                          objectFit: 'contain',
                        }}
                        src={resolveImageColor(seriesLogoNeg, 'white')}
                      />
                    ) : null}
                  </Link>

                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 5,
                      marginBottom: 10,
                    }}
                  >
                    <SafeImage
                      src={mailIcon}
                      style={{ width: 12, height: 12, objectFit: 'cover' }}
                    />
                    <Link
                      href={`mailto:${SPONSOR_EMAIL}`}
                      style={{
                        color: 'white',
                        fontSize: 12,
                        fontWeight: 400,
                        textDecoration: 'none',
                      }}
                    >
                      {SPONSOR_EMAIL}
                    </Link>
                  </View>

                  <Link
                    src={`${APP_HOST}/events/${event?.slug}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <Image
                      style={{
                        width: 120,
                        height: 40,
                        objectFit: 'contain',
                      }}
                      src={resolveImageColor(
                        getImageSrc(event?.images?.logoTransparent),
                        'white'
                      )}
                    />
                  </Link>
                </View>
              </View>
            </Page>
          );
        })}
    </>
  );
};

export default KeynotePage;
